import { Icon, Select, space, Toggle, Tooltip } from '@primer-io/goat'
import { Link, navigate } from 'gatsby'
import React, { useEffect } from 'react'
import styled from 'styled-components'
import { getLastUsedVersion, setLastUsedVersion } from '../../../utils/lastUsedSdkVersion'
import { Android } from './icons/Android'
import { IOS } from './icons/IOS'
import { ReactNative } from './icons/ReactNative'
import { Globe } from '@primer-io/goat-icons'

/** @param {{ platform: string, platforms: { [id: string]: Platform }, version: string }} */
export function SDK({ platform, platforms, version, ...props }) {
	const { latest, versions } = platforms[platform]
	const versionKeys = Object.keys(versions)

	useEffect(() => {
		if (versions[version]) setLastUsedVersion(platform, version)
	}, [platform, version])

	const platformOptions = versionKeys.map(v => ({
		value: v,
		label: `${v}${v === latest && ' (Latest)'}`,
	}))

	return (
		<Root {...props}>
			<Toggle.Root value={platform}>
				{Object.values(platforms).map(p => (
					<Tooltip key={p.id}>
						<Tooltip.Trigger
							as={ToggleGroupButton}
							// workaround, <Tooltip.Trigger> and <ToggleGroup.Button> both rely on [data-state]
							data-state={platform === p.id ? 'on' : undefined}
							to={`/sdk/${p.id}/${getLastUsedVersion(p.id) || p.latest}`}
							value={p.id}
						>
							<PlatformIcon platform={p.id} title={p.label} />
						</Tooltip.Trigger>
						<Tooltip.Content position='top'>{p.label}</Tooltip.Content>
					</Tooltip>
				))}
			</Toggle.Root>

			<Select.Layout>
				<Select
					options={platformOptions}
					value={platformOptions.find(p => p.value === version)}
					valueToString={item => item?.value ?? ''}
					labelToNode={item => item?.label ?? ''}
					searchFilter={(item, searchTerm) => !!item && item?.label?.toLowerCase().includes(searchTerm.toLowerCase())}
					disabled={versionKeys.length < 2}
					onChange={v => navigate(`/sdk/${platform}/${v.value}`)}
				>
					<Select.Trigger
						placeholder='Select'
						style={{
							maxWidth: '300px',
						}}
					/>
					<Select.Content>
						<Select.List />
					</Select.Content>
				</Select>
			</Select.Layout>
		</Root>
	)
}

const ToggleGroupButton = props => (
	<Toggle.Button
		{...props}
		as={Link}
		// workaround, <ToggleGroup.Button as="a"> doesn't seem to apply classNames properly
		className={['goat-Button goat-Button_outlined', props.className].filter(Boolean).join(' ')}
	/>
)

/** @param {{  platform: PlatformId }} */
function PlatformIcon({ platform, ...props }) {
	switch (platform) {
		case 'android':
			return <Android {...props} />
		case 'ios':
			return <IOS {...props} />
		case 'react-native':
			return <ReactNative {...props} />
		case 'web':
			return <Icon as={Globe} {...props} />
	}
}

const Root = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${space(3)};
	overflow: hidden;
	padding: ${space(6)} ${space(3)};
	width: 100%;

	.goat-ButtonGroup .goat-Button {
		place-content: center;
		width: 100%;
	}
`

/** @typedef {'android' | 'ios' | 'react-native' | 'web'} PlatformId */

/**
 * @typedef {Object} Platform
 * @property {string} id
 * @property {string} label
 * @property {string} latest
 * @property {{ [id: string]: Version }} versions
 */

/**
 * @typedef {Object} Version
 * @property {string} title
 */
